import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, switchMap } from 'rxjs';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import { ArkUserType, SystemFeatureModel, SystemFeatureSettingModel, SystemFeatureViewModelPrimeDatatableResults, PrimeDatatableParams } from '../../core/api/models';
import { SystemSettingsApiService } from '../../core/api/services';
import { Utils } from '../../core/utils';
import { AuthService } from '../../main/auth.service';
import { AlertService } from '../../shared/services/alert.service';

@Component({
  selector: 'app-system-features',
  templateUrl: './features.component.html',
  standalone: false
})
export class FeaturesComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private listRequest = 0;
  private orderRequest = 0;
  filter: PrimeDatatableParams = { first: 0, rows: 25, sortField: 'FeatureOrder', sortOrder: 1 };
  list?: SystemFeatureViewModelPrimeDatatableResults;
  currentItem?: SystemFeatureModel;
  loadingList = false;
  loadingItem = false;
  saving = false;
  visibleSettings: SystemFeatureSettingModel[] = [];
  hiddenSettings: SystemFeatureSettingModel[] = [];
  settingsScope: 'enterprise' | 'company' = 'enterprise';
  orderedFeatures: SystemFeatureModel[] = [];
  reorderVisible = false;
  loadingOrder = false;
  savingOrder = false;
  form: FormGroup;




  
  get featureIconValue() {
    return this.form.controls["Icon"].value;
  }
  constructor(
    private readonly api: SystemSettingsApiService,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly alertService: AlertService,
    formBuilder: FormBuilder
  ) {
    this.form = formBuilder.group({
      Name: ['', [Validators.required, Validators.maxLength(50)]],
      Title: ['', Validators.maxLength(255)],
      TitleGroup: ['', Validators.maxLength(255)],
      Icon: ['', Validators.maxLength(255)],
      Description: ['', Validators.maxLength(1000)],
      InitialValue: [false],
      FeatureOrder: [0, [Validators.required, Validators.min(0), Validators.max(2147483647), Validators.pattern(/^\d+$/)]]
    });
  }

  ngOnInit(): void {
    this.authService.checkIfAutorized([ArkUserType.Support, ArkUserType.Admin]);
    this.route.paramMap.pipe(
      switchMap(params => {
        const id = Number(params.get('ID'));
        this.currentItem = undefined;
        this.visibleSettings = [];
        this.hiddenSettings = [];
        this.loadingItem = id > 0;
        return id > 0 ? this.api.GetSystemFeature({ body: { id } }).pipe(
          catchError(error => {
            this.alertService.error(Utils.getAjaxError(error));
            return of(undefined);
          })
        ) : of(undefined);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(item => {
      this.currentItem = item;
      this.loadingItem = false;
      if (item) {
        this.form.reset(item);
        this.visibleSettings = (item.Settings ?? []).map(setting => ({ ...setting }));
        this.changeSettingsScope(this.settingsScope);
      }
    });
  }

  loadTable(event?: TableLazyLoadEvent): void {
    if (event) {
      this.filter = Utils.normalizePrimeDatatableEvent(event);
    }
    const request = ++this.listRequest;
    this.loadingList = true;
    this.api.ListSystemFeatures({ body: this.filter }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: result => {
        if (request !== this.listRequest) {
          return;
        }
        this.list = result;
        this.loadingList = false;
      },
      error: error => {
        if (request !== this.listRequest) {
          return;
        }
        this.loadingList = false;
        this.alertService.error(Utils.getAjaxError(error));
      }
    });
  }

  clear(table: Table): void {
    table.clear();
  }

  cancel(): void {
    if (!this.saving) {
      this.router.navigate(['/system-settings/features']);
    }
  }

  moveItem<T>(items: T[], index: number, target: number): void {
    if (this.saving || this.savingOrder || index < 0 || index >= items.length || target < 0 || target >= items.length) return;
    const settings = items === this.visibleSettings ? this.visibleSettings : items === this.hiddenSettings ? this.hiddenSettings : undefined;
    const orders = settings?.map(setting => setting.SettingOrder ?? 0).sort((left, right) => left - right);
    items.splice(target, 0, items.splice(index, 1)[0]);
    if (settings && orders) settings.forEach((setting, position) => setting.SettingOrder = orders[position]);
  }

  setSettingVisibility(setting: SystemFeatureSettingModel, visible: boolean): void {
    if (this.saving) return;
    const source = visible ? this.hiddenSettings : this.visibleSettings;
    const target = visible ? this.visibleSettings : this.hiddenSettings;
    const index = source.indexOf(setting);
    if (index < 0) return;
    source.splice(index, 1);
    if (this.settingsScope === 'enterprise') setting.ShowOnEnterpriseSettings = visible;
    else setting.ShowOnCompanySettings = visible;
    target.push(setting);
  }

  changeSettingsScope(scope: 'enterprise' | 'company'): void {
    const settings = [...this.visibleSettings, ...this.hiddenSettings].sort((left, right) => (left.SettingOrder ?? 0) - (right.SettingOrder ?? 0));
    settings.forEach((setting, index) => setting.SettingOrder = index + 1);
    this.settingsScope = scope;
    const isVisible = (setting: SystemFeatureSettingModel) => scope === 'enterprise'
      ? setting.ShowOnEnterpriseSettings : setting.ShowOnCompanySettings;
    this.visibleSettings = settings.filter(isVisible);
    this.hiddenSettings = settings.filter(setting => !isVisible(setting));
  }

  openFeatureOrder(): void {
    if (this.loadingOrder || this.savingOrder) return;
    const request = ++this.orderRequest;
    this.reorderVisible = true;
    this.loadingOrder = true;
    this.orderedFeatures = [];
    this.api.GetSystemFeatures().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: features => {
        if (request !== this.orderRequest) return;
        this.orderedFeatures = features.filter(x=>x.SystemSettings && x.SystemSettings>0);
        this.loadingOrder = false;
      },
      error: error => {
        if (request !== this.orderRequest) return;
        this.loadingOrder = false;
        this.closeFeatureOrder();
        this.alertService.error(Utils.getAjaxError(error));
      }
    });
  }

  closeFeatureOrder(): void {
    if (this.savingOrder) return;
    ++this.orderRequest;
    this.reorderVisible = false;
    this.loadingOrder = false;
  }

  saveFeatureOrder(): void {
    if (this.savingOrder || this.loadingOrder || !this.orderedFeatures.length) return;
    const submitted = this.orderedFeatures.map(feature => feature.ID ?? 0);
    this.savingOrder = true;
    this.api.SaveSystemFeatureOrder({ body: { SystemFeatureIDs: submitted } }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.savingOrder = false;
        this.closeFeatureOrder();
        this.loadTable();
        this.alertService.success('Feature order saved successfully.');
      },
      error: error => {
        this.savingOrder = false;
        this.alertService.error(Utils.getAjaxError(error));
      }
    });
  }

  save(): void {
    if (!this.currentItem || this.saving) {
      return;
    }
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.alertService.error('Enter a valid name, title, title group, icon, description and a non-negative whole number for the feature order.');
      return;
    }
    const submitted: SystemFeatureModel = {
      ...this.currentItem,
      ...this.form.getRawValue(),
      Settings: [...this.visibleSettings, ...this.hiddenSettings].map(setting => ({ ...setting }))
    };
    this.saving = true;
    this.api.SaveSystemFeature({ body: submitted }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: saved => {
        this.saving = false;
        if (this.currentItem?.ID === submitted.ID) {
          this.currentItem = saved;
          this.form.reset(saved);
          this.visibleSettings = (saved.Settings ?? []).map(setting => ({ ...setting }));
          this.hiddenSettings = [];
          this.changeSettingsScope(this.settingsScope);
        }
        this.alertService.success('Feature saved successfully.');
      },
      error: error => {
        this.saving = false;
        this.alertService.error(Utils.getAjaxError(error));
      }
    });
  }
}
